import { useState, useEffect } from 'react'
import { Modal } from 'almanac-ui/Modal'
import Styles from '../../../BlogMetadata.module.scss'
import { AddImage } from 'almanac-ui/icons/16/AddImage'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { VisuallyHidden } from '@react-aria/visually-hidden'

interface FeaturedMediaSelectModalProps {
  images: string[]
  selectedImage?: string
  onSelect: (image: string) => void
  onClose: () => void
  onImageUpload: (url: string) => void
}

export const FeaturedMediaSelectModal = ({
  images,
  selectedImage,
  onSelect,
  onClose,
  onImageUpload,
}: FeaturedMediaSelectModalProps) => {
  const [selected, setSelected] = useState<string | undefined>(selectedImage)
  const [localImages, setLocalImages] = useState<string[]>(images)

  // Sync selected state with selectedImage prop
  useEffect(() => {
    setSelected(selectedImage)
  }, [selectedImage])

  // Sync local images with images prop
  useEffect(() => {
    setLocalImages(images)
  }, [images])

  // Upload logic
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const { fileInputRef, uploadImagesFromEvent, isUploading } = useImportMediaFiles(false, (files) => {
    if (files && files.length > 0 && files[0].source_url) {
      const newImageUrl = files[0].source_url
      onImageUpload(newImageUrl)
      setLocalImages((prev) => [...prev, newImageUrl])
    }
  })

  // Only allow one image upload at a time
  const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (!uploadsProjectId) return
    if (event.target.files && event.target.files.length > 1) {
      const dt = new DataTransfer()
      dt.items.add(event.target.files[0])
      event.target.files = dt.files
    }
    uploadImagesFromEvent(event, uploadsProjectId)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <Modal.Root size="sm" onClose={onClose} wrapperClassName={Styles.featuredMediaModalWrapper}>
      <Modal.Header className={Styles.featuredMediaModalHeader}>Change Featured Image</Modal.Header>
      <Modal.Content>
        <div className={Styles.featuredMediaListWrapper}>
          <div className={Styles.featuredMediaList}>
            <button
              key="upload-image"
              className={Styles.uploadMediaItem}
              type="button"
              aria-label="Upload image"
              onClick={() => {
                if (fileInputRef.current) fileInputRef.current.click()
              }}
              disabled={isUploading || !uploadsProjectId}
            >
              <AddImage size={32} color="var(--selected-stroke)" />
            </button>
            <VisuallyHidden>
              <input
                aria-label="Upload images"
                ref={fileInputRef}
                type="file"
                multiple={false}
                onChange={handleOnChange}
                accept="image/*"
              />
            </VisuallyHidden>
            {localImages.map((img) => (
              <button
                key={img}
                className={selected === img ? Styles.selectedMedia : Styles.mediaItem}
                onClick={() => setSelected(img)}
                type="button"
              >
                <img src={img} alt="Featured option" className={Styles.mediaImg} />
              </button>
            ))}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" onPress={onClose}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right" className={Styles.footerButtons}>
          <Modal.FooterButton
            variant="primary"
            onPress={() => selected && onSelect(selected)}
            size="lg"
            isDisabled={!selected || isUploading}
          >
            Confirm
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
